import React, { useState } from "react";
import { Heart, MessageCircle, Bookmark, Share2, MoreHorizontal, ArrowRight, Trash2, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, timeAgo } from "@/lib/api";
import { useAuth, useRequireAuth } from "@/context/AuthContext";
import { VerifiedProfessionalBadge, MedLyncVerifiedBadge, CategoryPill } from "@/components/Badges";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function UpdateCard({ update, onChange }) {
  const { user } = useAuth();
  const requireAuth = useRequireAuth();
  const navigate = useNavigate();
  const [u, setU] = useState(update);
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const initials = (u.author_name || "M").split(" ").map((w) => w[0]).slice(0, 2).join("");
  const isAdmin = user && user.role === "admin";
  const canDelete = isAdmin || (user && user.id === u.author_id);

  const toggleLike = () => requireAuth(async () => {
    const { data } = await api.post(`/updates/${u.id}/like`);
    setU(data);
  });
  const toggleSave = () => requireAuth(async () => {
    const { data } = await api.post(`/updates/${u.id}/save`);
    setU(data);
    toast.success(data.saved ? "Saved" : "Removed from saved");
  });
  const share = async () => {
    const url = `${window.location.origin}/?u=${u.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.info(url); }
  };
  const openComments = async () => {
    setOpen(true);
    const { data } = await api.get(`/updates/${u.id}/comments`);
    setComments(data);
  };
  const submitComment = () => requireAuth(async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/updates/${u.id}/comments`, { body: text });
      setComments((c) => [...c, data]);
      setText("");
      setU((p) => ({ ...p, comment_count: p.comment_count + 1 }));
    } finally { setBusy(false); }
  });
  const toggleVerify = async () => {
    const { data } = await api.patch(`/updates/${u.id}/verify`);
    setU((p) => ({ ...p, medlync_verified: data.medlync_verified }));
    toast.success(data.medlync_verified ? "Marked MedLync-Verified" : "Verification removed");
  };
  const remove = async () => {
    await api.delete(`/updates/${u.id}`);
    toast.success("Update deleted");
    onChange && onChange();
  };

  return (
    <article data-testid="news-update-card" className="ml-rise space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-slate-300">
      {/* author row */}
      <div className="flex items-center gap-2.5">
        <Avatar className="h-9 w-9">
          <AvatarFallback className="bg-navy text-xs font-bold text-white">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-sm font-bold text-slate-900">{u.author_name}</p>
            {u.author_verified_professional && <VerifiedProfessionalBadge compact />}
          </div>
          <p className="truncate text-xs text-slate-500">{u.author_specialty || "MedLync"} · {timeAgo(u.created_at)}</p>
        </div>
        {canDelete && (
          <button onClick={remove} className="text-slate-300 hover:text-red-500" title="Delete"><Trash2 className="h-4 w-4" /></button>
        )}
      </div>

      {/* category + content verification */}
      <div className="flex flex-wrap items-center gap-2">
        <CategoryPill category={u.category} />
        {u.medlync_verified && <MedLyncVerifiedBadge compact />}
        {isAdmin && (
          <button onClick={toggleVerify}
            className={cn("ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
              u.medlync_verified ? "border-orange-300 bg-orange-50 text-orange-800" : "border-slate-200 text-slate-500 hover:bg-slate-50")}>
            <Check className="h-3 w-3" /> {u.medlync_verified ? "Verified" : "Verify content"}
          </button>
        )}
      </div>

      <div className="flex gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-bold leading-snug text-slate-900">{u.title}</h3>
          <p className="mt-1 line-clamp-3 text-sm leading-relaxed text-slate-600">{u.summary || u.body}</p>
        </div>
        {u.cover_url && (
          <img src={fileUrl(u.cover_url)} alt="" className="h-24 w-24 shrink-0 rounded-xl border border-slate-200 object-cover" />
        )}
      </div>

      {u.tags && u.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {u.tags.map((t) => <span key={t} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">#{t}</span>)}
        </div>
      )}

      {/* Govt & Health hub CTA */}
      {u.is_govt_health && u.hub_slug && (
        <button onClick={() => navigate(`/govt-health/${u.hub_slug}`)}
          className="flex w-full items-center justify-between rounded-xl bg-navy px-4 py-3 text-left text-white transition-colors hover:bg-navy-hover">
          <span className="text-sm font-bold">Explore this hub on MedLync</span>
          <ArrowRight className="h-4 w-4" />
        </button>
      )}

      {/* actions */}
      <div className="flex items-center gap-5 border-t border-slate-100 pt-3 text-slate-500">
        <button data-testid="update-like-button" onClick={toggleLike} className={cn("flex items-center gap-1.5 text-sm transition-colors", u.liked ? "text-medorange" : "hover:text-navy")}>
          <Heart className={cn("h-4 w-4", u.liked && "fill-medorange")} /> {u.like_count}
        </button>
        <button data-testid="update-comment-button" onClick={openComments} className="flex items-center gap-1.5 text-sm hover:text-navy">
          <MessageCircle className="h-4 w-4" /> {u.comment_count}
        </button>
        <button data-testid="update-save-button" onClick={toggleSave} className={cn("flex items-center gap-1.5 text-sm transition-colors", u.saved ? "text-medorange" : "hover:text-navy")}>
          <Bookmark className={cn("h-4 w-4", u.saved && "fill-medorange")} /> Save
        </button>
        <button data-testid="update-share-button" onClick={share} className="ml-auto flex items-center gap-1.5 text-sm hover:text-navy">
          <Share2 className="h-4 w-4" /> Share
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="comments-dialog">
          <DialogHeader><DialogTitle className="font-heading">Discussion</DialogTitle></DialogHeader>
          <div className="max-h-72 space-y-3 overflow-y-auto">
            {comments.length === 0 && <p className="py-6 text-center text-sm text-slate-400">Be the first to comment.</p>}
            {comments.map((c) => (
              <div key={c.id} className="rounded-xl bg-slate-50 p-3">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-bold text-slate-800">{c.author_name}</p>
                  {c.author_verified_professional && <VerifiedProfessionalBadge compact />}
                  <span className="text-[11px] text-slate-400">· {timeAgo(c.created_at)}</span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{c.body}</p>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <Textarea data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" className="min-h-[44px] resize-none" />
            <Button data-testid="comment-submit-button" onClick={submitComment} disabled={busy} className="bg-medorange hover:bg-medorange-hover">Post</Button>
          </div>
        </DialogContent>
      </Dialog>
    </article>
  );
}
